import {
    Divider,
    Flex,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useTheme,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import apiClient from '../../../canopy-ui-components/api';
import CanopyButton from '../../../canopy-ui-components/components/CanopyButton/CanopyButton';
import CanopyIconButton from '../../../canopy-ui-components/components/IconButton/IconButton';
import * as ga from '../../../canopy-ui-components/google-analytics';
import { useCurrentColors } from '../../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
function DeleteStrategyModal({
    data,
    showModal,
    setShowModal,
    globalProps,
    refreshData,
}: {
    data?: any[];
    showModal?: boolean;
    setShowModal?: (flag: boolean) => void;
    globalProps?: GlobalProps;
    refreshData?: (args?: any) => void;
}) {
    const currentColors = useCurrentColors();
    const { t } = globalProps || {};
    const [isLoading, setIsLoading] = useState(false);
    const [systemStrtegies, setSystemStrategies] = useState<any>();
    const [nonSystemStrtegies, setNonSystemStrategies] = useState<any>();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const theme = useTheme();

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        const strategyIds = data?.reduce((acc: any[], item) => {
            if (!item?.system_generated_strategy) {
                acc.push(item.id);
            }
            return acc;
        }, []);
        let response: any;
        try {
            response = await apiClient.delete('strategies', {
                queryParams: {
                    strategy_ids: strategyIds?.join(',') || '',
                },
            });

            setIsLoading(false);
            setShowModal && setShowModal(false);
            response = { success: response?.data };
        } catch (e) {
            setIsLoading(false);
            setShowModal && setShowModal(false);
            response = { success: false };
        }
        let toastInfo = {};
        if (response?.success === true) {
            toastInfo = {
                title: t('ui.mm.strategy-delete-success', {
                    count: data?.length,
                }),
                backgroundColor: currentColors.green['500'],
            };
        } else {
            toastInfo = {
                title: t('ui.mm.strategy-delete-error', {
                    count: data?.length,
                }),
                backgroundColor: currentColors.errorText,
            };
        }
        setShowModal && setShowModal(false);
        refreshData &&
            refreshData({
                ...toastInfo,
            });
    };

    const handleOnClose = () => {
        ga.event({
            action: 'Portfolio > Manage Strategies > Close Delete Strategy',
        });
        setShowModal && setShowModal(false);
    };

    useEffect(() => {
        showModal ? onOpen() : onClose();
    }, [showModal]);

    useEffect(() => {
        setSystemStrategies(
            data?.filter((item) => item?.system_generated_strategy)
        );
        setNonSystemStrategies(
            data?.filter((item) => !item?.system_generated_strategy)
        );
    }, [data]);

    return (
        <Modal
            isCentered
            closeOnOverlayClick={false}
            onEsc={() => setShowModal && setShowModal(false)}
            isOpen={isOpen}
            size="2xl"
            onClose={handleOnClose}>
            <ModalOverlay
                style={{
                    backdropFilter: 'blur(5px)',
                }}
            />
            <ModalContent>
                <ModalHeader
                    color={currentColors.brand['10']}
                    fontSize="20px"
                    fontWeight="bold"
                    padding="15px">
                    <Flex alignItems="center">
                        <Image
                            src={theme?.images?.headerLogo}
                            maxWidth="100%"
                            width="auto"
                            height={'35px'}
                            mr="17.5px"
                        />
                        <Text width="100%">
                            {t('ui.mm.delete-strategy', {
                                count: data?.length,
                            })}
                        </Text>
                    </Flex>
                    <Divider
                        borderColor={currentColors.brand['700']}
                        width="100%"
                        mb="0px"
                    />
                </ModalHeader>
                <ModalCloseButton
                    as={() => (
                        <CanopyIconButton
                            background="transparent"
                            cursor="pointer"
                            border="none"
                            display="inline-flex"
                            onClick={handleOnClose}
                            color={currentColors.brand['500']}
                            width="24px"
                            height="24px"
                            aria-label="remove"
                            icon="ActClose"
                            variant="ghost"
                            position="absolute"
                            top="15px"
                            right="15px"
                            isDisabled={isLoading}
                        />
                    )}
                />
                <ModalBody padding="0 15px">
                    {systemStrtegies?.length ? (
                        <>
                            <Text
                                mb="15px"
                                fontSize="14px">
                                {systemStrtegies?.length === data?.length
                                    ? t('ui.mm.no-strategies-to-delete')
                                    : t(
                                          'ui.mm.system-generated-strategy-cannot-be-deleted',
                                          {
                                              count: systemStrtegies?.length,
                                          }
                                      )}
                            </Text>
                            <TableContainer
                                maxHeight="60vh"
                                overflow="auto"
                                overflowY="auto">
                                <Table variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>
                                                {t(
                                                    'ui.mm.table-headers.strategy-name'
                                                )}
                                            </Th>
                                            <Th>
                                                {t(
                                                    'ui.mm.table-headers.system_generated_strategy'
                                                )}
                                            </Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {systemStrtegies?.map((item: any) => {
                                            return (
                                                <Tr key={item?.name}>
                                                    <Td>{item?.name}</Td>
                                                    <Td>
                                                        {String(
                                                            item?.system_generated_strategy
                                                        )}
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : (
                        <Text>
                            {t('ui.mm.confirm-strategy-delete', {
                                count: data?.length,
                            })}
                        </Text>
                    )}
                </ModalBody>

                {data?.length ? (
                    <ModalFooter
                        flexDirection="column"
                        p="15px">
                        <Divider
                            mt="0"
                            mb="15px"
                        />

                        <HStack
                            width="100%"
                            spacing="15px"
                            justifyContent="flex-end">
                            <CanopyButton
                                fontSize="sm"
                                width="100px"
                                fontWeight="bold"
                                variant="outline"
                                isDisabled={isLoading}
                                onClick={handleOnClose}>
                                {t('cancel')}
                            </CanopyButton>
                            <CanopyButton
                                data-test="deleteButton"
                                fontSize="sm"
                                width="100px"
                                fontWeight="bold"
                                isDisabled={
                                    isLoading ||
                                    data?.length === systemStrtegies?.length
                                }
                                isLoading={isLoading}
                                onClick={handleConfirmDelete}>
                                {t('delete')}
                            </CanopyButton>
                        </HStack>
                    </ModalFooter>
                ) : null}
            </ModalContent>
        </Modal>
    );
}

export default DeleteStrategyModal;
